// src/services/transactionServiceExtension.js
// This file extends the transaction service with functions for end-of-day features
import axios from 'axios';
import tenantStorage from './tenantStorage';

console.log('API_URL in transactionServiceExtension:', process.env.REACT_APP_API_URL);

const API_URL = (() => {
    const url = (process.env.REACT_APP_API_URL || '').trim();

    if (!url) {
        // Auto-detect based on current domain (fallback when env var is not set)
        if (window.location.hostname.includes('depos-frontend-staging-h76dptzhyq-ew.a.run.app')) {
            return 'https://depos-backend-staging-h76dptzhyq-ew.a.run.app';
        } else if (window.location.hostname.includes('depos-frontend-h76dptzhyq-ew.a.run.app')) {
            return 'https://depos-backend-h76dptzhyq-ew.a.run.app';
        } else if (window.location.hostname === 'aqana.uk' || window.location.hostname === 'www.aqana.uk') {
            return 'https://api.aqana.uk';
        } else if (window.location.hostname === 'staging.aqana.uk') {
            return 'https://depos-backend-staging-h76dptzhyq-ew.a.run.app';
        }
        return 'http://localhost:8000';
    }

    // If it's a localhost URL, ensure it uses HTTP; otherwise use HTTPS
    if (url.includes('localhost') || url.includes('127.0.0.1')) {
        return url.startsWith('https://') ? url.replace('https://', 'http://') : url;
    } else {
        return url.startsWith('http://') ? url.replace('http://', 'https://') : url;
    }
})();

const api = axios.create({
    baseURL: API_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Ensure all requests use HTTPS for non-localhost URLs
api.interceptors.request.use(config => {
    if (config.baseURL && config.baseURL.startsWith('http://') &&
        !config.baseURL.includes('localhost') && !config.baseURL.includes('127.0.0.1')) {
        config.baseURL = config.baseURL.replace('http://', 'https://');
    }
    if (config.url && config.url.startsWith('http://') &&
        !config.url.includes('localhost') && !config.url.includes('127.0.0.1')) {
        config.url = config.url.replace('http://', 'https://');
    }
    return config;
}, error => Promise.reject(error));

api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => Promise.reject(error)
);

// Add this function to the transactionService object in api.js
const getTransactionsBySalesDate = async (date) => {
    try {
        const response = await api.get(`/transactions/sales-date/${encodeURIComponent(date)}`);
        return response.data;
    } catch (error) {
        throw error;
    }
};

// Function to create a transaction with sales date information
const createTransactionWithSalesDate = async (transactionData) => {
    try {
        // If sales_date isn't set, use the current date or from tenantStorage
        if (!transactionData.sales_date) {
            try {
                const salesDate = await tenantStorage.getState('salesDate');
                transactionData.sales_date = salesDate || transactionData.date;
            } catch (error) {
                console.error('Failed to get sales date from tenant storage:', error);
                transactionData.sales_date = transactionData.date;
            }
        }

        const response = await api.post('/transactions', transactionData);
        return response.data;
    } catch (error) {
        throw error;
    }
};

// Generate end of day report based on sales date
const generateEndOfDayReport = async (salesDate) => {
    try {
        // Get all transactions for the sales date
        const transactions = await getTransactionsBySalesDate(salesDate);

        // Initialize counters and structures
        const paymentMethodCounts = {};
        const paymentMethodTotals = {};
        const diningOptionCounts = {};
        const diningOptionTotals = {};
        const productCounts = {};
        const productTotals = {};
        let grandTotal = 0;
        let totalTransactions = transactions.length;

        // Process each transaction
        transactions.forEach(transaction => {
            // Count payment methods
            const paymentMethod = transaction.payment_method;
            paymentMethodCounts[paymentMethod] = (paymentMethodCounts[paymentMethod] || 0) + 1;
            paymentMethodTotals[paymentMethod] = (paymentMethodTotals[paymentMethod] || 0) + parseFloat(transaction.final_amount);

            // Count dining options
            const diningOption = transaction.dining_option;
            diningOptionCounts[diningOption] = (diningOptionCounts[diningOption] || 0) + 1;
            diningOptionTotals[diningOption] = (diningOptionTotals[diningOption] || 0) + parseFloat(transaction.final_amount);

            // Add to grand total
            grandTotal += parseFloat(transaction.final_amount);

            // Process items in the transaction
            if (transaction.items && transaction.items.length) {
                transaction.items.forEach(item => {
                    const productName = item.product_name;
                    const itemQuantity = parseInt(item.quantity);
                    const itemTotal = parseFloat(item.total_amount);

                    productCounts[productName] = (productCounts[productName] || 0) + itemQuantity;
                    productTotals[productName] = (productTotals[productName] || 0) + itemTotal;
                });
            }
        });

        return {
            paymentMethodCounts,
            paymentMethodTotals,
            diningOptionCounts,
            diningOptionTotals,
            productCounts,
            productTotals,
            grandTotal,
            totalTransactions,
            date: salesDate,
            timestamp: new Date().toISOString()
        };
    } catch (error) {
        console.error('Error generating end of day report:', error);
        throw error;
    }
};

const createEndOfDayReport = async (reportData) => {
    try {
        // Log the data being sent
        console.log('Sending end-of-day report data:', JSON.stringify(reportData, null, 2));

        // Get the token from localStorage
        const token = localStorage.getItem('token');

        // Make the fetch request directly - note the trailing slash
        const response = await fetch(`${API_URL}/end-of-day/`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': token ? `Bearer ${token}` : '',
            },
            body: JSON.stringify(reportData),
        });

        // Get the response text for debugging
        const responseText = await response.text();

        if (!response.ok) {
            console.error('Server error response:', responseText);
            throw new Error(`HTTP error! Status: ${response.status}, Details: ${responseText}`);
        }

        // Try to parse the response as JSON or return text
        try {
            return JSON.parse(responseText);
        } catch (e) {
            console.warn('Response is not valid JSON:', responseText);
            return { success: true, message: responseText };
        }
    } catch (error) {
        console.error('Error creating end of day report:', error);
        throw error;
    }
};

const createSimplifiedEndOfDayReport = async (summaryData) => {
    try {
        // Get the token from localStorage
        const token = localStorage.getItem('token');

        // Make the fetch request with JSON data
        const response = await fetch(`${API_URL}/end-of-day/summary`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': token ? `Bearer ${token}` : '',
            },
            body: JSON.stringify(summaryData),
        });

        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`HTTP error! Status: ${response.status}, Details: ${errorText}`);
        }

        const result = await response.json();
        return result;
    } catch (error) {
        console.error('Error creating simplified end of day report:', error);
        throw error;
    }
};

// Add to exported functions
export const transactionServiceExtension = {
    getTransactionsBySalesDate,
    createTransactionWithSalesDate,
    generateEndOfDayReport,
    createEndOfDayReport,
    createSimplifiedEndOfDayReport
};
